import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { ArrowLeft, Heart, PhoneCall, ShieldAlert, Timer } from 'lucide-react';

const ResponsibleGamblingPage = () => (
  <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
    <Header />
    <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full" data-testid="responsible-gambling-page">
      <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>
      <h1 className="text-3xl font-black uppercase text-white mb-2" style={{ fontFamily: 'Outfit' }}>
        Responsible <span className="text-[#D4AF37]">Gambling</span>
      </h1>
      <p className="text-gray-400 mb-8">At KrazyBets, player safety comes before profit. Always.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="card-dark flex items-start gap-3">
          <Heart className="h-6 w-6 text-[#D4AF37] mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-white font-bold">Stay In Control</h3>
            <p className="text-sm text-gray-400 mt-1">Only wager what you can afford to lose. Gambling should be entertainment, not a source of income.</p>
          </div>
        </div>
        <div className="card-dark flex items-start gap-3">
          <Timer className="h-6 w-6 text-[#D4AF37] mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-white font-bold">Set Limits</h3>
            <p className="text-sm text-gray-400 mt-1">Use our deposit and session limits (Settings → Limits) to stay within your budget.</p>
          </div>
        </div>
        <div className="card-dark flex items-start gap-3">
          <ShieldAlert className="h-6 w-6 text-[#D4AF37] mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-white font-bold">Self-Exclude Anytime</h3>
            <p className="text-sm text-gray-400 mt-1">Request a cool-off of 24 hours, 7 days, 30 days or permanent via our Support team.</p>
          </div>
        </div>
        <div className="card-dark flex items-start gap-3">
          <PhoneCall className="h-6 w-6 text-[#D4AF37] mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-white font-bold">Need Help?</h3>
            <p className="text-sm text-gray-400 mt-1">Speak to someone. BeGambleAware, GambleSA, and other helplines are listed below.</p>
          </div>
        </div>
      </div>

      <div className="card-dark space-y-6 text-gray-300 text-sm leading-relaxed">
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Our Commitment</h2>
          <p>KrazyBets is committed to providing a safe, fair and transparent betting environment. We enforce strict 18+ verification, KYC, AI-driven risk monitoring and daily bet limits for free tier users. Our platform bans bets related to minors, violence, adult content or any illegal activity.</p>
        </section>
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Warning Signs of Problem Gambling</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Spending more money or time on betting than you planned</li>
            <li>Chasing losses with bigger stakes</li>
            <li>Lying to family or friends about your betting activity</li>
            <li>Betting interfering with work, studies or relationships</li>
            <li>Feeling anxious, guilty or depressed after placing bets</li>
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Tools We Offer</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Daily deposit limits (Settings → Responsible Gambling)</li>
            <li>Session time reminders</li>
            <li>Self-exclusion for 24h / 7d / 30d / permanent</li>
            <li>Reality check pop-ups after extended sessions</li>
            <li>Account freeze via Support in under 24 hours</li>
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Help Lines</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><span className="text-[#D4AF37] font-semibold">South Africa:</span> National Responsible Gambling Programme — 0800 006 008</li>
            <li><span className="text-[#D4AF37] font-semibold">UK:</span> GamCare — 0808 8020 133 / BeGambleAware.org</li>
            <li><span className="text-[#D4AF37] font-semibold">USA:</span> National Council on Problem Gambling — 1-800-522-4700</li>
            <li><span className="text-[#D4AF37] font-semibold">Nigeria:</span> Mentally Aware Initiative — 0800 6636 2255</li>
            <li><span className="text-[#D4AF37] font-semibold">Global:</span> Gamblers Anonymous — gamblersanonymous.org</li>
          </ul>
        </section>
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Underage Gambling — Zero Tolerance</h2>
          <p>You must be 18 years or older to use KrazyBets. Any account found to be under 18 will be permanently closed and all funds forfeited. We use KYC verification, IP checks and payment method screening to prevent underage access.</p>
        </section>
        <section>
          <h2 className="text-lg font-bold text-white mb-2">Contact Us</h2>
          <p>If you, or someone you know, needs help, please email <a href="mailto:info@krazybets.online" className="text-[#D4AF37] hover:underline">info@krazybets.online</a>. All self-exclusion requests are processed confidentially within 24 hours.</p>
        </section>
        <div className="border-t border-[#3D3D3D] pt-4 mt-4">
          <p className="text-gray-500 text-xs">© 2026 KrazyBets. 18+ Only. Gamble Responsibly. Owned and trademarked by Qfinity Media Group.</p>
        </div>
      </div>
    </main>
    <Footer />
  </div>
);

export default ResponsibleGamblingPage;
